import React, { useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddServicesIncludedPage = () => {

  // =========================================
  // NAVIGATE
  // =========================================

  const navigate = useNavigate();

  // =========================================
  // SAVING LOADER
  // =========================================

  const [isSaving, setIsSaving] = useState(false);

  // =========================================
  // SERVICES INCLUDED ROWS
  // =========================================

  const [serviceRows, setServiceRows] = useState([
    {
      name: "",
      icon_file: null,
    },
  ]);

  // =========================================
  // ADD ROW
  // =========================================

  const addServiceRow = () => {

    // Don't allow adding while saving
    if (isSaving) {
      return;
    }

    setServiceRows((prevRows) => [
      ...prevRows,
      {
        name: "",
        icon_file: null,
      },
    ]);
  };

  // =========================================
  // REMOVE ROW
  // =========================================

  const removeServiceRow = async (index) => {

    // Don't allow remove while saving
    if (isSaving) {
      return;
    }

    // At least one row should remain
    if (serviceRows.length === 1) {

      toast.error(
        "At least one row is required"
      );

      return;
    }

    // Confirmation
    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to remove this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setServiceRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================================
  // HANDLE SERVICE NAME CHANGE
  // =========================================

  const handleServiceNameChange = (
    index,
    value
  ) => {

    if (isSaving) {
      return;
    }

    setServiceRows((prevRows) => {

      const updatedRows = [...prevRows];

      updatedRows[index] = {
        ...updatedRows[index],
        name: value,
      };

      return updatedRows;
    });
  };

  // =========================================
  // HANDLE ICON CHANGE
  // =========================================

  const handleIconChange = (
    index,
    file
  ) => {

    if (isSaving) {
      return;
    }

    setServiceRows((prevRows) => {

      const updatedRows = [...prevRows];

      updatedRows[index] = {
        ...updatedRows[index],
        icon_file: file,
      };

      return updatedRows;
    });
  };

  // =========================================
  // VALIDATE SERVICE ROWS
  // =========================================

  const validateServiceRows = () => {

    // =========================================
    // CHECK SERVICE NAME
    // =========================================

    const hasEmptyName = serviceRows.some(
      (row) =>
        String(row?.name ?? "").trim() === ""
    );

    if (hasEmptyName) {

      toast.error(
        "Services Included Name is required"
      );

      return false;
    }

    // =========================================
    // CHECK ICON
    // =========================================

    const hasEmptyIcon = serviceRows.some(
      (row) => !row?.icon_file
    );

    if (hasEmptyIcon) {

      toast.error(
        "Services Included Icon is required"
      );

      return false;
    }

    // =========================================
    // CHECK DUPLICATE SERVICE NAME
    // =========================================

    const serviceMap = new Map();

    for (const row of serviceRows) {

      const serviceName = String(
        row.name
      )
        .trim()
        .toLowerCase();

      if (serviceMap.has(serviceName)) {

        toast.error(
          `Duplicate service name: ${row.name}`
        );

        return false;
      }

      serviceMap.set(
        serviceName,
        true
      );
    }

    return true;
  };

  // =========================================
  // SAVE SERVICES INCLUDED
  // =========================================

  const saveServicesIncluded = async () => {

    // =========================================
    // PREVENT DOUBLE SUBMIT
    // =========================================

    if (isSaving) {
      return;
    }

    // =========================================
    // BASIC VALIDATION
    // =========================================

    if (
      !Array.isArray(serviceRows) ||
      serviceRows.length === 0
    ) {

      toast.error(
        "Services Included is required"
      );

      return;
    }

    // =========================================
    // VALIDATE ROWS
    // =========================================

    if (!validateServiceRows()) {
      return;
    }

    // =========================================
    // CONFIRMATION
    // =========================================

    const confirm = await Swal.fire({
      title: "Save Services Included?",
      text: "Do you want to save these Services Included?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d6efd",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    // =========================================
    // START LOADER
    // =========================================

    setIsSaving(true);

    try {

      // =========================================
      // FORM DATA
      // =========================================

      const formData = new FormData();

      // =========================================
      // APPEND NAME + ICON
      // =========================================

      serviceRows.forEach((row) => {

        formData.append(
          "names[]",
          row.name.trim()
        );

        formData.append(
          "icon_files",
          row.icon_file
        );

      });

      // =========================================
      // DEBUG
      // =========================================

      console.log(
        "SERVICE ROWS =>",
        serviceRows
      );

      // =========================================
      // API CALL
      // =========================================

      const res = await api.post(
        "/adminHolidays/holidays-services-included-save",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log(
        "SERVICES INCLUDED SAVE RESPONSE =>",
        res?.data
      );

      // =========================================
      // SUCCESS
      // =========================================

      if (res?.data?.success) {

        await Swal.fire({
          title: "Success",
          text:
            res?.data?.message ||
            "Services Included Saved Successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        // =========================================
        // RESET
        // =========================================

        setServiceRows([
          {
            name: "",
            icon_file: null,
          },
        ]);

        // =========================================
        // NAVIGATE
        // =========================================

        navigate(
          "/holidays/services-included/manage-services-included"
        );

      } else {

        await Swal.fire({
          title: "Error",
          text:
            res?.data?.message ||
            "Unable to save Services Included",
          icon: "error",
          confirmButtonText: "OK",
        });

      }

    } catch (error) {

      console.error(
        "Save Services Included Error =>",
        error
      );

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Server Error";

      toast.error(errorMessage);

    } finally {

      // =========================================
      // STOP LOADER
      // =========================================

      setIsSaving(false);

    }
  };

  // =========================================
  // JSX
  // =========================================

  return (
    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* =========================================
          HEADER
      ========================================= */}

      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">

        <div className="card-body d-flex justify-content-between align-items-center">

          <h4 className="fw-bold mb-0">
            Add Services Included
          </h4>

          <Link
            to="/holidays/services-included/manage-services-included"
            className={`btn btn-outline-secondary btn-sm d-flex align-items-center gap-2 ${
              isSaving
                ? "pe-none opacity-50"
                : ""
            }`}
          >
            <ArrowLeft size={16} />
            Back
          </Link>

        </div>

      </div>

      {/* =========================================
          TABLE
      ========================================= */}

      <div className="row">

        <div className="col-lg-12">

          <div className="card shadow-sm border-0">

            <div className="card-body">

              <div className="table-responsive">

                <table className="table table-hover table-bordered align-middle mb-0">

                  {/* =========================================
                      TABLE HEADER
                  ========================================= */}

                  <thead className="table-light">

                    <tr>

                      <th
                        width="70"
                        className="text-center"
                      >
                        Sl.No
                      </th>

                      <th className="text-center">
                        Services Included Name
                      </th>

                      <th className="text-center">
                        Services Included Icon
                      </th>

                      <th
                        width="140"
                        className="text-center"
                      >
                        Action
                      </th>

                    </tr>

                  </thead>

                  {/* =========================================
                      TABLE BODY
                  ========================================= */}

                  <tbody>

                    {serviceRows.map(
                      (row, index) => (

                        <tr key={index}>

                          {/* =================================
                              SERIAL NUMBER
                          ================================= */}

                          <td className="text-center fw-semibold">
                            {index + 1}
                          </td>

                          {/* =================================
                              SERVICE NAME
                          ================================= */}

                          <td>

                            <input
                              type="text"
                              placeholder="Enter Services Name"
                              className="form-control"
                              value={row.name}
                              disabled={isSaving}
                              onChange={(e) =>
                                handleServiceNameChange(
                                  index,
                                  e.target.value
                                )
                              }
                            />

                          </td>

                          {/* =================================
                              SERVICE ICON
                          ================================= */}

                          <td>

                            <input
                              type="file"
                              className="form-control"
                              accept="image/*"
                              disabled={isSaving}
                              onChange={(e) =>
                                handleIconChange(
                                  index,
                                  e.target.files?.[0] ||
                                    null
                                )
                              }
                            />

                            {row.icon_file && (

                              <small className="text-muted d-block mt-1">

                                Selected:{" "}
                                {row.icon_file.name}

                              </small>

                            )}

                          </td>

                          {/* =================================
                              ACTION
                          ================================= */}

                          <td className="text-center">

                            {/* ADD */}

                            <button
                              type="button"
                              className="btn btn-success btn-sm me-2"
                              onClick={addServiceRow}
                              disabled={isSaving}
                              title="Add Row"
                            >

                              <i className="fa fa-plus-square"></i>

                            </button>

                            {/* DELETE */}

                            <button
                              type="button"
                              className="btn btn-danger btn-sm"
                              onClick={() =>
                                removeServiceRow(index)
                              }
                              disabled={isSaving}
                              title="Delete Row"
                            >

                              <i className="fa fa-trash"></i>

                            </button>

                          </td>

                        </tr>

                      )
                    )}

                  </tbody>

                </table>

              </div>

            </div>

          </div>

        </div>

        {/* =========================================
            SAVE BUTTON
        ========================================= */}

        <div className="col-lg-12 text-center">

          <button
            type="button"
            className="btn btn-info text-white px-5 mt-3"
            onClick={saveServicesIncluded}
            disabled={isSaving}
          >

            {isSaving ? (

              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>

                Uploading & Saving...

              </>

            ) : (

              <>
                <i className="fa fa-save me-2"></i>

                Save Services Included
              </>

            )}

          </button>

        </div>

      </div>

      {/* =========================================
          FULL PAGE LOADER
      ========================================= */}

      {/* {isSaving && (

        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(255,255,255,0.75)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexDirection: "column",
          }}
        >

          <div
            className="spinner-border text-primary"
            role="status"
            style={{
              width: "3rem",
              height: "3rem",
            }}
          >
            <span className="visually-hidden">
              Loading...
            </span>
          </div>

          <div className="mt-3 fw-semibold text-primary">
            Uploading files and saving...
          </div>

          <div className="text-muted small mt-1">
            Please wait, do not close or refresh this page.
          </div>

        </div>

      )} */}

    </div>
  );
};

export default AddServicesIncludedPage;